import React, { useContext, createContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AuthService from "../../services/authService";
import UserService from "../../services/userService";
import ApiService from "../../apiService";

const AuthContext = createContext();

const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  // El JWT ya no se guarda en localStorage — vive como cookie httpOnly en el browser
  // token en estado: true = sesión activa, "" = sin sesión
  const [token, setToken] = useState("");
  const [user, setUser] = useState(null);
  const [rol, setRol] = useState(null);
  // Mientras se verifica la sesión en mount no renderizamos rutas protegidas
  const [sessionLoading, setSessionLoading] = useState(true);

  // Al montar: verificar si existe cookie de sesión válida para restaurar el estado
  useEffect(() => {
    const restoreSession = async () => {
      const savedUsername = localStorage.getItem("username");
      if (savedUsername) {
        try {
          await ApiService.get("/auth/verify");
          setToken(true);
          setUser(savedUsername);
          setRol(localStorage.getItem("userRol") || null);

          // Si el usuario tiene sesión pero está en la pantalla de login,
          // redirigirlo automáticamente a su pantalla correspondiente.
          const onLoginPage = ["/", "/Login", "/login", "/login"].includes(window.location.pathname);
          if (onLoginPage) {
            const savedRoles = JSON.parse(localStorage.getItem("userRoles") || "[]");
            const turnosPath = getTurnosRedirectFromRoles(savedRoles);
            if (turnosPath) {
              navigate(turnosPath, { replace: true });
            } else {
              navigate("/Home", { replace: true });
            }
          }
        } catch {
          // Cookie expirada o inválida — limpiar datos locales
          clearLocalStorage();
        }
      }
      setSessionLoading(false);
    };
    restoreSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Guardas globales de cierre de pestaña/navegador:
  //  - beforeunload: si hay un turno en atención (flag seteado por OperadorPage
  //    en sessionStorage) el navegador muestra su diálogo de confirmación.
  //    Funciona desde cualquier página de la intranet.
  //  - pagehide: desactiva al operador vía sendBeacon SOLO cuando la página
  //    realmente se descarga. (Antes se hacía en beforeunload y el operador
  //    quedaba inactivo aunque cancelara el cierre en el diálogo.)
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (sessionStorage.getItem("turno_en_atencion")) {
        e.preventDefault();
        // Requerido por Chrome/Edge para mostrar el diálogo de confirmación
        e.returnValue =
          "Tenés un turno en atención. Finalizalo o derivalo antes de salir.";
        return e.returnValue;
      }
    };

    const handlePageHide = () => {
      const userId = localStorage.getItem("userId");
      if (!userId) return;

      // sendBeacon es la forma más confiable de enviar datos al cerrar
      const baseUrl = process.env.REACT_APP_API_URL || "";
      const url = `${baseUrl}/turnos/operadores/desactivar-por-logout`;
      const data = JSON.stringify({ userId: parseInt(userId, 10) });
      if (navigator.sendBeacon) {
        const blob = new Blob([data], { type: "application/json" });
        navigator.sendBeacon(url, blob);
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("pagehide", handlePageHide);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("pagehide", handlePageHide);
    };
  }, []);

  const clearLocalStorage = () => {
    localStorage.removeItem("username");
    localStorage.removeItem("screenname");
    localStorage.removeItem("userId");
    localStorage.removeItem("userRol");
    localStorage.removeItem("userRoles");
    localStorage.removeItem("bossId");
    localStorage.removeItem("newUser");
    localStorage.removeItem("userSucursal");
    sessionStorage.removeItem("turnos_session");
    sessionStorage.removeItem("turno_en_atencion");
  };

  /**
   * Detecta si el usuario tiene un rol especial de turnos y devuelve
   * la ruta a la que debe ir directamente al hacer login.
   * Retorna null si no aplica ninguna regla especial.
   */
  const getTurnosRedirectFromRoles = (roles = []) => {
    const rolesLower = roles.map((r) => r.toLowerCase());
    if (rolesLower.some((r) => r.includes("display")))  return "/display";
    if (rolesLower.some((r) => r.includes("kiosco") || r.includes("turnero"))) return "/kiosco";
    return null;
  };

  const loginAction = async (data) => {
    try {
      const response = await AuthService.login(data);
      const roleResponse = await UserService.getUserRole(response.data.id);

      if (response) {
        // El JWT llega como cookie httpOnly — no se guarda en localStorage
        localStorage.setItem("username", response.data.username);
        localStorage.setItem("screenname", response.data.screenname);
        localStorage.setItem("userId", response.data.id);
        localStorage.setItem("userRoles", JSON.stringify(roleResponse.roles || []));
        localStorage.setItem("userRol", (roleResponse.roles || []).join(", "));
        localStorage.setItem("bossId", response.data.manager.id);
        if (response.data.sucursal) {
          localStorage.setItem("userSucursal", response.data.sucursal);
        }
        setToken(true);
        setUser(response.data.username);
        setRol("admin");

        if (response.data.newUser === true) {
          navigate("/mi-perfil");
          localStorage.setItem("newUser", "true");
        } else {
          const turnosPath = getTurnosRedirectFromRoles(roleResponse.roles || []);
          navigate(turnosPath ?? "/Home", { replace: true });
        }

        return response;
      }
      return response;
    } catch (error) {
      console.log(error);
      const resp   = error?.response?.data?.resp;
      const adCode = error?.response?.data?.adCode;
      const message = resp
        ? adCode && adCode !== "desconocido"
          ? `${resp} (cod. ${adCode})`
          : resp
        : "Error al iniciar sesión. Verificá tus credenciales.";
      // Adjuntar adCode al error para que LoginContainer pueda leerlo
      const err = new Error(message);
      err.adCode = adCode || null;
      throw err;
    }
  };

  /**
   * Usado después de un cambio de contraseña exitoso.
   * El backend ya hizo el login y devuelve los mismos datos que /login.
   * Seteamos el estado local igual que en loginAction.
   */
  const loginAfterPasswordChange = async (apiResponse) => {
    try {
      // apiResponse = { data: { id, username, ... }, msg: "..." }
      // igual estructura que loginAction recibe de AuthService.login
      const data = apiResponse.data ?? apiResponse;
      const roleResponse = await UserService.getUserRole(data.id);

      localStorage.setItem("username",   data.username);
      localStorage.setItem("screenname", data.screenname);
      localStorage.setItem("userId",     data.id);
      localStorage.setItem("userRoles",  JSON.stringify(roleResponse.roles || []));
      localStorage.setItem("userRol",    (roleResponse.roles || []).join(", "));
      localStorage.setItem("bossId",     data.manager.id);
      if (data.sucursal) {
        localStorage.setItem("userSucursal", data.sucursal);
      }
      setToken(true);
      setUser(data.username);
      setRol("admin");

      const turnosPath = getTurnosRedirectFromRoles(roleResponse.roles || []);
      navigate(turnosPath ?? "/Home", { replace: true });
    } catch (error) {
      console.error("[loginAfterPasswordChange]", error);
      throw new Error("No se pudo completar el ingreso tras el cambio de contraseña.");
    }
  };

  const logout = async () => {
    try {
      const userId = localStorage.getItem("userId");
      await ApiService.post("/auth/logout", { userId: userId ? parseInt(userId, 10) : null });
    } catch (error) {
      // El backend rechaza el logout si el operador tiene un turno en atención:
      // NO limpiar la sesión local y propagar para que el caller avise al usuario.
      if (error?.response?.status === 409 && error?.response?.data?.code === "TURNO_ACTIVO") {
        const err = new Error(
          error.response.data.message ||
            "No puede cerrar sesión con un turno en atención. Finalice o derive el turno primero.",
        );
        err.code = "TURNO_ACTIVO";
        throw err;
      }
      // Otros errores: continuar con logout local aunque falle el endpoint
    }
    setToken("");
    setUser(null);
    setRol(null);
    clearLocalStorage();
  };

  return (
    <AuthContext.Provider value={{ token, user, rol, sessionLoading, loginAction, loginAfterPasswordChange, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;

export const useAuth = () => {
  return useContext(AuthContext);
};
